<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    header="Set Up Agent"
    :dismissable="false"
    :style="{ width: '28rem' }"
  >
    <div class="mb-6 flex items-start gap-4">
      <div
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-blue-50 text-lg"
      >
        <i class="pi pi-download" />
      </div>
      <div>
        <h2 class="text-navy-800 mb-1 text-[17px] font-semibold">
          Install an Agent
        </h2>
        <p class="text-navy-500 text-sm leading-relaxed">
          Choose which agent to install on a Windows machine on your network,
          then download and run the installer.
        </p>
      </div>
    </div>

    <!-- Agent type selection -->
    <div class="mb-6 space-y-3">
      <button
        v-for="agent in agentTypes"
        :key="agent.id"
        type="button"
        class="flex w-full cursor-pointer items-center gap-4 rounded-lg border-2 p-4 text-left transition-colors"
        :class="
          selectedAgent === agent.id
            ? 'border-green-500 bg-green-50/50'
            : 'border-neutral-200 hover:border-neutral-300'
        "
        @click="selectedAgent = agent.id"
      >
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-lg"
          :class="
            selectedAgent === agent.id
              ? 'bg-green-100 text-green-700'
              : 'bg-neutral-100 text-neutral-500'
          "
        >
          <i :class="agent.icon" />
        </div>
        <div class="flex-1">
          <div class="text-navy-800 text-sm font-semibold">
            {{ agent.name }}
          </div>
          <div class="text-navy-500 text-xs">{{ agent.description }}</div>
        </div>
        <div
          v-if="selectedAgent === agent.id"
          class="flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-white"
        >
          <i class="pi pi-check text-xs" />
        </div>
      </button>
    </div>

    <!-- Download link -->
    <div v-if="selectedAgent" class="mb-6">
      <a
        :href="`/api/agent/installer?company_id=${companyId}`"
        class="inline-flex items-center gap-2 rounded-md border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
      >
        <i class="pi pi-download text-xs" />
        Download Installer
        <span v-if="agentVersion" class="text-xs text-neutral-400">
          v{{ agentVersion }}
        </span>
      </a>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <AppButton variant="secondary" @click="visible = false">
          Cancel
        </AppButton>
        <AppButton
          :disabled="!selectedAgent"
          :loading="generating"
          @click="handleContinue"
        >
          Continue to Pairing
        </AppButton>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import Drawer from "primevue/drawer"
import { useToast } from "primevue/usetoast"
import { onMounted, ref } from "vue"

import GENERATE_PAIRING_CODE from "../../queries/agent_registration/generatePairingCode.gql"
import { apolloClient } from "../../queries/client"
import AppButton from "../ui/AppButton.vue"

export interface PairingCodeResult {
  code: string
  expiresAt: string
}

interface Props {
  companyId: string
}

const props = defineProps<Props>()

const visible = defineModel<boolean>("visible", { default: false })

const emit = defineEmits<{
  continue: [result: PairingCodeResult]
}>()

const toast = useToast()

const agentTypes = [
  {
    id: "softpro",
    name: "SoftPro Select",
    description: "Automated document processing and SoftPro integration",
    icon: "pi pi-building",
  },
]

const selectedAgent = ref<string | null>(null)
const agentVersion = ref<string | null>(null)
const generating = ref(false)

const fetchAgentVersion = async () => {
  try {
    const resp = await fetch(
      `/api/agent/installer/version?company_id=${props.companyId}`,
    )
    if (resp.ok) {
      const data = await resp.json()
      agentVersion.value = data.version ?? null
    }
  } catch {
    // Version display is best-effort
  }
}

onMounted(() => {
  void fetchAgentVersion()
})

const handleContinue = async () => {
  generating.value = true
  try {
    const result = await apolloClient.mutate({
      mutation: GENERATE_PAIRING_CODE,
      variables: { companyId: props.companyId },
    })
    const data = result.data?.generatePairingCode
    if (data?.errors?.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: data.errors.join(", "),
        life: 4000,
      })
      return
    }
    emit("continue", { code: data.code, expiresAt: data.expiresAt })
  } catch {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to generate pairing code",
      life: 4000,
    })
  } finally {
    generating.value = false
  }
}
</script>
